'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { hrefFor, isExternalLink } from '../landing-v2/studyHelpers';
import type { Study } from 'content/science/studyTypes';
import styles from './RelatedStudies.module.css';

/* "Related studies" band at the foot of a white-paper detail page.
 *
 * Compact by design. The listing's own WhitePaperCard was tried here and is
 * simply the wrong size for a footer — 380px tall with a 22px title, it
 * competes with the article it is meant to trail. What carries over instead
 * is the part that makes it recognisable: the white / grey / dark surface
 * rotation and its per-variant tokens, copied from that card so the two
 * cannot drift apart on colour.
 *
 * Everything else is scaled down — 14px titles, an 11px tag, a 32px arrow —
 * so three cards sit under the references without shouting.
 *
 * Selection is editor-curated first, then same-category fill. The fill is
 * what makes the section exist before anyone has curated anything, and keeps
 * the row full when an editor picks only one or two.
 */

type Variant = 'white' | 'grey' | 'dark';

interface Props {
  /* Slugs the editor curated in the CMS, in their order. */
  curatedSlugs?: string[];
  /* Every study, for resolving curated slugs and for the fill. */
  pool: Study[];
  currentSlug: string;
  currentCategory?: string;
}

const TAKE = 3;

/* First three of the listing's six-step rotation. */
const VARIANTS: Variant[] = ['white', 'grey', 'dark'];

/* Routed through the SAME science.card.* keys WhitePaperCard uses — a
   deliberate divergence from the source's hardcoded English switch, so the
   two card families cannot drift. */
const READ_LABEL_KEY: Record<Study['kind'], string> = {
  'white-paper': 'science.card.readWhitePaper',
  'peer-reviewed': 'science.card.readJournalArticle',
  medrxiv: 'science.card.readPreprint',
  'conference-poster': 'science.card.viewPoster',
  snapshot: 'science.card.readByte',
};

const RelatedStudies = ({
  curatedSlugs = [],
  pool,
  currentSlug,
  currentCategory,
}: Props) => {
  const t = useTranslations('science');
  const items = useMemo(() => {
    const candidates = pool.filter((s) => s.slug !== currentSlug);
    const seen = new Set<string>();
    const out: Study[] = [];

    const push = (s: Study) => {
      if (seen.has(s.slug) || out.length >= TAKE) return;
      seen.add(s.slug);
      out.push(s);
    };

    curatedSlugs.forEach((slug) => {
      const hit = candidates.find((s) => s.slug === slug);
      if (hit) push(hit);
    });

    const byDate = (a: Study, b: Study) =>
      (b.publishedAt || '').localeCompare(a.publishedAt || '');

    if (out.length < TAKE && currentCategory) {
      candidates
        .filter((s) => s.category === currentCategory)
        .sort(byDate)
        .forEach(push);
    }
    /* A thin category should not leave a half-empty row. */
    if (out.length < TAKE) {
      [...candidates].sort(byDate).forEach(push);
    }
    return out;
  }, [curatedSlugs, pool, currentSlug, currentCategory]);

  if (items.length === 0) return null;

  return (
    <section
      className={styles.section}
      aria-labelledby="related-studies-heading"
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.eyebrow}>{t('science.related.moreStudies')}</p>
          <h2 className={styles.heading} id="related-studies-heading">
            {t('science.related.keepExploring')}
          </h2>
        </div>
        <div className={styles.grid}>
          {items.map((s, i) => {
            const variant = VARIANTS[i % VARIANTS.length];
            const inner = (
              <>
                <span className={styles.top}>
                  <span className={styles.pill}>{s.categoryLabel}</span>
                  <span className={styles.dateText}>{s.publishedAtLabel}</span>
                </span>
                <span className={styles.title}>{s.title}</span>
                <span className={styles.foot}>
                  <span className={styles.readLabel}>
                    {t(READ_LABEL_KEY[s.kind] ?? 'science.card.readMore')}
                  </span>
                  <span className={styles.arrow} data-arrow-badge="true">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M7 17 17 7M8 7h9v9" />
                    </svg>
                  </span>
                </span>
              </>
            );

            return isExternalLink(s) ? (
              <a
                key={s.slug}
                className={styles.cardAnchor}
                data-variant={variant}
                href={hrefFor(s)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {inner}
              </a>
            ) : (
              <Link
                key={s.slug}
                className={styles.cardLink}
                data-variant={variant}
                href={hrefFor(s)}
              >
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedStudies;
